// Remote support — coordinate mapping (shared/REMOTE-SUPPORT.md §4/§6). Pure.
//
// Helper side: the remote screen is drawn LETTERBOXED (object-fit: contain) inside the viewer; a pointer
// position in the viewer becomes x,y normalised 0..1 over the VIDEO FRAME (= the shared display), or
// nothing when it is over the bars. Requester side (the supporthost test harness; the natives have their
// own twins): normalised → pixels on the shared display, which may sit anywhere on a virtual desktop.

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Where a `videoW`×`videoH` frame lands when fitted (contain) into a `boxW`×`boxH` box. */
export function letterbox(boxW: number, boxH: number, videoW: number, videoH: number): Rect {
  if (!(boxW > 0 && boxH > 0)) return { x: 0, y: 0, w: 0, h: 0 };
  if (!(videoW > 0 && videoH > 0)) return { x: 0, y: 0, w: boxW, h: boxH };
  // The constrained side is the box's own (exact, no rounding residue): bars on the other axis only.
  if (boxW / videoW <= boxH / videoH) {
    const h = (videoH * boxW) / videoW;
    return { x: 0, y: (boxH - h) / 2, w: boxW, h };
  }
  const w = (videoW * boxH) / videoH;
  return { x: (boxW - w) / 2, y: 0, w, h: boxH };
}

/**
 * A point in the box's coordinates → normalised over the frame. `inside` is false over the letterbox bars;
 * the point is still clamped onto the frame's edge so a drag that leaves the picture keeps tracking the edge
 * (a button-up outside must still land somewhere sane).
 */
export function toNormalised(px: number, py: number, frame: Rect): { x: number; y: number; inside: boolean } {
  if (!(frame.w > 0 && frame.h > 0)) return { x: 0, y: 0, inside: false };
  const rx = (px - frame.x) / frame.w;
  const ry = (py - frame.y) / frame.h;
  const inside = rx >= 0 && rx <= 1 && ry >= 0 && ry <= 1;
  const c = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
  return { x: c(rx), y: c(ry), inside };
}

/**
 * Normalised → a pixel on the shared display. `display` is that display's rectangle in the requester's
 * desktop coordinates (x/y may be negative on a monitor left of / above the primary). 1.0 maps to the LAST
 * pixel, never one past it (which would fall on the neighbouring monitor).
 */
export function toDisplayPixel(x: number, y: number, display: Rect): { x: number; y: number } {
  const c = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
  const px = Math.min(display.w - 1, Math.floor(c(x) * display.w));
  const py = Math.min(display.h - 1, Math.floor(c(y) * display.h));
  return { x: display.x + Math.max(0, px), y: display.y + Math.max(0, py) };
}
